import React, { useState, useEffect } from 'react';
import { auth, setupRecaptcha, signInWithGoogle } from '../firebase';
import { signInWithPhoneNumber, linkWithPhoneNumber } from 'firebase/auth';
import { X, Phone, MessageSquare, Loader, Check } from 'lucide-react';
import { FcGoogle } from 'react-icons/fc';
import { useTranslation, Trans } from 'react-i18next'; // <-- Import useTranslation and Trans
import HjezlyLogo from './HjezlyLogo';

const CustomerLoginModal = ({ onClose, onLoginSuccess }) => {
  const { t, i18n } = useTranslation(); // <-- Add the hook
  const [step, setStep] = useState('main');
  const [phoneNumber, setPhoneNumber] = useState('+961');
  const [otp, setOtp] = useState('');
  const [loading, setLoading] = useState(null);
  const [error, setError] = useState('');
  const [confirmationResult, setConfirmationResult] = useState(null);
  const [googleUser, setGoogleUser] = useState(null);

  // Cleanup reCAPTCHA when modal is unmounted
  useEffect(() => {
    return () => {
      if (window.recaptchaVerifier) {
        try {
          window.recaptchaVerifier.clear();
        } catch (err) {
          console.warn("Error clearing reCAPTCHA on unmount:", err);
        }
        window.recaptchaVerifier = null;
      }
    };
  }, []);

  // Initialize reCAPTCHA when phone step is shown
  useEffect(() => {
    if (step === 'phone') {
      // Small delay to ensure DOM is ready. 
      // We will now target the GLOBAL container which is always in the index.html
      const timer = setTimeout(() => {
        try {
          if (!window.recaptchaVerifier) {
            console.log("Initializing reCAPTCHA for phone step...");
            // Use global container ID
            setupRecaptcha('global-recaptcha-container');
          }
        } catch (err) {
          console.error("Error initializing reCAPTCHA:", err);
        }
      }, 300);
      
      return () => clearTimeout(timer);
    }
  }, [step]);

  const handleGoogleSignIn = async () => {
    setError('');
    setLoading('google');
    try {
      const user = await signInWithGoogle();
      if (user.phoneNumber) {
        setStep('success');
        setTimeout(() => onLoginSuccess(user), 1500);
      } else {
        setGoogleUser(user);
        setStep('phone');
      }
      setLoading(null);
    } catch (err) {
      console.error(err);
      setError(t('loginmodal.errorDefault'));
      setLoading(null);
    }
  };

  const handleSendOtp = async () => {
    setError('');
    setLoading('phone');

    // Remove spaces and any non-digit characters except + for validation
    const cleanedPhoneNumber = phoneNumber.replace(/[\s\-()]/g, '');
    
    const testNumber = '+96112345678';
    const isTestNumber = (cleanedPhoneNumber === testNumber);
    const validLebaneseNumber = /^\+961[0-9]{8}$/; 
    const isRealNumber = validLebaneseNumber.test(cleanedPhoneNumber);

    if (!isTestNumber && !isRealNumber) {
      if (!cleanedPhoneNumber.startsWith('+961')) {
         setError(t('loginmodal.errorPhonePrefix'));
      } else if (cleanedPhoneNumber.length !== 12) {
         setError(t('loginmodal.errorPhoneLength', { count: cleanedPhoneNumber.length - 4 }));
      } else {
         setError(t('loginmodal.errorPhoneFormat'));
      }
      setLoading(false);
      return;
    }

    try {
      // Use existing reCAPTCHA verifier (initialized in useEffect)
      const verifier = window.recaptchaVerifier;
      if (!verifier) {
        throw new Error("reCAPTCHA not initialized. Please refresh and try again.");
      }
      
      console.log("Sending verification code to:", cleanedPhoneNumber);
      let result;
      if (googleUser) {
        result = await linkWithPhoneNumber(googleUser, cleanedPhoneNumber, verifier);
      } else {
        result = await signInWithPhoneNumber(auth, cleanedPhoneNumber, verifier);
      }
      console.log("Verification code sent successfully");
      setConfirmationResult(result);
      setStep('otp');
      setLoading(null);
    } catch (err) {
      console.error('FIREBASE AUTH ERROR:', err.code, err.message, err);
      
      // If internal error, it might be due to reCAPTCHA issues. 
      // We should NOT clear the verifier blindly if it's just an SMS failure, 
      // but for internal errors, a refresh is often needed.
      if (err.code === 'auth/internal-error') {
         // Force clear so it re-initializes next time
         if (window.recaptchaVerifier) {
            try { window.recaptchaVerifier.clear(); } catch(e) {}
            window.recaptchaVerifier = null;
         }
      } else if (window.recaptchaVerifier) {
          // For other errors like invalid phone number, we might just want to reset
          // Check if reset function exists before calling
          try {
            if (typeof window.recaptchaVerifier.reset === 'function') {
                window.recaptchaVerifier.reset();
            } else {
                window.recaptchaVerifier.clear();
                window.recaptchaVerifier = null;
            }
          } catch (clearErr) {
            console.warn("Error resetting reCAPTCHA:", clearErr);
          }
      }
      
      let userMessage = t('loginmodal.errorDefault');
      if (err.code === 'auth/invalid-phone-number') {
        userMessage = t('loginmodal.errorInvalidPhone');
      } else if (err.code === 'auth/too-many-requests') {
        userMessage = t('loginmodal.errorTooManyRequests');
      } else if (err.code === 'auth/network-request-failed') {
        userMessage = t('loginmodal.errorNetwork');
      } else if (err.code === 'auth/captcha-check-failed') {
        userMessage = t('loginmodal.errorCaptcha');
      } else if (err.code === 'auth/quota-exceeded') {
        userMessage = t('loginmodal.errorQuota');
      } else if (err.code === 'auth/operation-not-allowed') {
        userMessage = t('loginmodal.errorQuota'); // Using quota error as it's similar
      }
      
      setError(userMessage);
      setLoading(null);
    }
  };

  const handleVerifyOtp = async () => {
    setError('');
    setLoading('otp');
    
    // Remove spaces and any non-digit characters from OTP
    const cleanedOtp = otp.replace(/[\s-]/g, '');
    
    if (cleanedOtp.length !== 6) {
      setError(t('loginmodal.errorInvalidCode'));
      setLoading(null);
      return;
    }

    try {
      const result = await confirmationResult.confirm(cleanedOtp);
      setStep('success');
      setTimeout(() => {
        onLoginSuccess(result.user);
      }, 1500);
    } catch (err) {
      console.error('Error verifying OTP:', err);
      setError(t('loginmodal.errorInvalidCode'));
      setLoading(null);
    }
  };
  
  // Close Button component (removed unused variable warning)
  const CloseButton = () => (
    <button
      onClick={onClose}
      className="absolute top-4 right-4 text-neutral-500 hover:text-white transition-colors"
    >
      <X size={24} />
    </button>
  );

  // --- RENDER STEPS ---
  return (
    <div className={`fixed inset-0 bg-black/60 backdrop-blur-sm flex items-start justify-center p-4 z-[60] animate-fade-in overflow-y-auto ${i18n.language === 'ar' ? 'text-right' : 'text-left'}`} dir={i18n.language === 'ar' ? 'rtl' : 'ltr'}>
      
      <div className="relative w-full max-w-md bg-neutral-900 border border-neutral-800 rounded-2xl shadow-2xl p-8 animate-fadeIn">
        
        {/* Close Button */}
        <CloseButton />

        {/* Modal Content */}
        <div className="text-center mb-8">
          <div className="flex justify-center mb-4">
             <HjezlyLogo size="md" variant="icon" /> 
          </div>
          <h2 className="text-2xl font-bold text-white mb-2 font-display">
            {step === 'main' && t('customerloginmodal.welcome')}
            {step === 'phone' && t('customerloginmodal.enterphone')}
            {step === 'otp' && t('customerloginmodal.verifyotp')}
            {step === 'success' && t('customerloginmodal.success')}
          </h2>
          <p className="text-neutral-400 text-sm">
            {step === 'main' && t('customerloginmodal.welcomemsg')}
            {step === 'phone' && t('customerloginmodal.entermsg')}
            {step === 'otp' && t('customerloginmodal.verifymsg')}
            {step === 'success' && t('customerloginmodal.successmsg')}
          </p>
        </div>

        {/* --- STEP 1: MAIN SELECTION --- */}
        {step === 'main' && (
          <div className="space-y-4">
            {error && (
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-400 px-4 py-3 rounded-lg text-center">
                {error}
              </div>
            )}
            
            <button
              onClick={handleGoogleSignIn}
              disabled={!!loading}
              className="w-full flex items-center justify-center gap-3 px-6 py-4 bg-white dark:bg-neutral-800 border-2 border-neutral-300 dark:border-neutral-600 rounded-xl font-semibold text-neutral-800 dark:text-white hover:bg-neutral-50 dark:hover:bg-neutral-700 transition"
            >
              {loading === 'google' ? (
                <Loader size={24} className="animate-spin" />
              ) : (
                <FcGoogle size={24} />
              )}
              {t('loginmodal.googleButton')}
            </button>
            
            <button
              onClick={() => setStep('phone')}
              disabled={!!loading}
              className="w-full flex items-center justify-center gap-3 px-6 py-4 bg-neutral-800 dark:bg-neutral-700 text-white rounded-xl font-semibold hover:bg-neutral-700 dark:hover:bg-neutral-600 transition"
            >
              <Phone size={20} />
              {t('loginmodal.phoneButton')}
            </button>
          </div>
        )}

        {/* --- STEP 2: PHONE NUMBER --- */}
        {(step === 'phone') && (
          <div className="space-y-6">
            {error && (
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-400 px-4 py-3 rounded-lg text-center">
                {error}
              </div>
            )}

            <div>
              <label className="block text-sm font-semibold text-neutral-300 mb-2">
                {t('loginmodal.phoneLabel')}
              </label>
              <div className="relative">
                <div className={`absolute ${i18n.language === 'ar' ? 'right-3.5' : 'left-3.5'} top-1/2 transform -translate-y-1/2 z-10`}>
                  <Phone 
                    size={20} 
                    style={{ 
                      color: '#D4AF37',
                      stroke: '#D4AF37',
                      strokeWidth: 2.5,
                      filter: 'drop-shadow(0 0 2px rgba(0,0,0,0.5))'
                    }}
                  />
                </div>
                <input
                  type="tel"
                  value={phoneNumber}
                  onChange={(e) => setPhoneNumber(e.target.value)}
                  className={`w-full ${i18n.language === 'ar' ? 'pr-12 pl-4' : 'pl-12 pr-4'} py-4 border-2 border-neutral-700 dark:bg-neutral-900 dark:text-white rounded-xl focus:ring-2 focus:ring-primary focus:border-transparent transition`}
                  placeholder="+961 3 123 456"
                />
              </div>
            </div>

            <button
              onClick={handleSendOtp}
              disabled={!!loading}
              className="w-full flex items-center justify-center gap-2 px-6 py-4 bg-primary text-white rounded-xl font-semibold hover:bg-primary-dark transition shadow-lg disabled:opacity-50"
            >
              {loading === 'phone' ? (
                <>
                  <Loader size={20} className="animate-spin" />
                  <span>{t('loginmodal.sendingCode')}</span>
                </>
              ) : (
                t('loginmodal.sendCode')
              )}
            </button>
            <p className="text-xs text-neutral-400 text-center mt-4">
              <Trans i18nKey="loginmodal.legal">
                This site is protected by reCAPTCHA and the Google
                <a href="https://policies.google.com/privacy" target="_blank" rel="noreferrer" className="hover:underline">Privacy Policy</a> 
                and 
                <a href="https://policies.google.com/terms" target="_blank" rel="noreferrer" className="hover:underline">Terms of Service</a> 
                apply.
              </Trans>
            </p>
          </div>
        )}
        
        {/* --- STEP 3: OTP VERIFICATION --- */}
        {step === 'otp' && (
          <div className="space-y-6">
            {error && (
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-400 px-4 py-3 rounded-lg text-center">
                {error}
              </div>
            )}

            <div>
              <label className="block text-sm font-semibold text-neutral-300 mb-2">
                {t('loginmodal.otpLabel')}
              </label>
              <div className="relative">
                <MessageSquare size={20} className={`absolute ${i18n.language === 'ar' ? 'right-4' : 'left-4'} top-1/2 transform -translate-y-1/2 text-neutral-400`} />
                <input
                  type="tel"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value)}
                  maxLength={6}
                  className={`w-full ${i18n.language === 'ar' ? 'pr-12 pl-4' : 'pl-12 pr-4'} py-4 border-2 border-neutral-700 dark:bg-neutral-900 dark:text-white rounded-xl focus:ring-2 focus:ring-primary focus:border-transparent transition`}
                  placeholder="123456"
                />
              </div>
            </div>

            <button
              onClick={handleVerifyOtp}
              disabled={!!loading}
              className="w-full flex items-center justify-center gap-2 px-6 py-4 bg-primary text-white rounded-xl font-semibold hover:bg-primary-dark transition shadow-lg disabled:opacity-50"
            >
              {loading === 'otp' ? (
                <>
                  <Loader size={20} className="animate-spin" />
                  <span>{t('loginmodal.verifying')}</span>
                </>
              ) : (
                t('loginmodal.verify')
              )}
            </button>

            <button
              onClick={() => { setStep('phone'); setError(''); }}
              className="w-full text-center text-sm text-neutral-400 hover:text-primary"
            >
              {t('loginmodal.differentNumber')}
            </button>
          </div>
        )}

        {/* --- STEP 4: SUCCESS --- */}
        {step === 'success' && (
          <div className="text-center py-8">
            <div className="w-20 h-20 bg-primary/20 dark:bg-primary/30 rounded-full flex items-center justify-center mx-auto mb-6">
              <Check size={48} className="text-primary" />
            </div>
            <h2 className="text-3xl font-bold text-neutral-800 dark:text-white mb-3">
              {googleUser ? t('loginmodal.successTitleVerified') : t('loginmodal.successTitle')}
            </h2>
            <p className="text-lg text-neutral-600 dark:text-neutral-400">
              {t('loginmodal.successSubtitle')}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default CustomerLoginModal;